import { useFormik } from 'formik';
import Button from '../../components/button/Button';
import Checkbox from '../../components/checkBox/CheckBox';
import Card from '../../components/card/Card';
import DatePicker from '../../components/datepicker/DatePicker';
import DropDown from '../../components/dropdown/DropDown';
import Input from '../../components/input/Input';
import SearchableSelect from '../../components/sercheableselect/SearchableSelect';
import P from '../../components/texts/p/P';
import Span from '../../components/texts/span/Span';
import {
  basicData,
  clientData,
  corporateStructureData,
} from '../../types/interfaces';
import { useEffect, useState, useMemo, useRef } from 'react';
import {
  getClientInfo,
  loadAssets,
  saveClientDocument,
  downloadClientDocument,
  saveClientInfo,
} from '../../services/ClientService';
import { provinces } from '../../mocks/Provinces';
import { ivaStatuses } from '../../mocks/IvaStatuses';
import { legalEntities } from '../../mocks/LegalEntities';
import { formatCuit } from '../../utils/Masks';
import Icon from '../../components/icon/Icon';
import Image from '../../components/image/Image';
import Spinner from '../../components/Spinner/spinner';
import { economicActivities } from '../../mocks/EconomicActivities';
 import { initialClientData, MAX_FILE_SIZE, APPLICATION_STATUS } from '../../utils/Constants';
import useStatus from '../../hooks/useStatus';
import { NumericFormat } from 'react-number-format';
import { getApplicationsByCuit } from '../../services/ApplicationService';
import { useLocation, useParams } from 'react-router-dom';
import { useOfficers } from '../../hooks/useOfficers';
import { useReferrerNames } from '../../hooks/useReferrerNames';
import { ReferrerName } from '../../types/interfaces';
import { useReferrerPermissions } from '../../hooks/useReferrerPermissions';
import ReferrersManagementModal from '../../components/modals/ReferrersManagementModal';
import { useToast } from '../../components/toast/toast';
import { useCategoryTypes } from '../../hooks/useCategoryTypes';

interface BasicDataProps {
  basicData: clientData;
  onSave: Function;
  onSendStatus: Function;
  isSaving: boolean;
  applicationStatus: number;
  isValidatedClient: Function;
  currentStep?: number;
  previousStep?: number;
  onDataChange?: (clientInfo: clientData) => void;
}

type activity = {
  code: number;
  label: string;
};

const BasicData: React.FC<BasicDataProps> = ({
  basicData,
  onSave,
  onSendStatus,
  isSaving,
  applicationStatus,
  isValidatedClient,
  currentStep,
  previousStep,
  onDataChange,
}) => {
  const { isApproved, isRejected, isInCommittee, isFinished } = useStatus({
    applicationStatus,
  });
  const { referrerNames, isLoading: isLoadingReferrerNames, refreshReferrerNames } = useReferrerNames();
  const { officers, isLoading: isLoadingOfficers } = useOfficers();
  const { hasPermission } = useReferrerPermissions();
  const { categoryTypes, isLoading: isLoadingCategoryTypes } = useCategoryTypes();
  const [isReferrersModalOpen, setIsReferrersModalOpen] = useState(false);
  
  // Obtener configuración
  const config = JSON.parse(localStorage.getItem('config') ?? '{}');
  const isClientCategoryTypeEnabled = config?.isClientCategoryTypeEnabled ?? false;
  
  const [errorMsg, setErrorMsg] = useState('');
  const [isClientFound, setIsClientFound] = useState<boolean>(false);
  const [isApplicationOnCourse, setIsApplicationOnCourse] =
    useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [hasPendingApplication, setHasPendingApplication] = useState<boolean>(false);
  const [isCuitValid, setIsCuitValid] = useState<boolean>(true);
  const [validatedBasicData, setValidatedBasicData] = useState<boolean>(true);
  const [uploadingAssetIndex, setUploadingAssetIndex] = useState<number | null>(null);
  const [assetUploadError, setAssetUploadError] = useState<{ [key: number]: string }>({});
  const [hasClientBeenSaved, setHasClientBeenSaved] = useState<boolean>(false);
  const [searchedCuit, setSearchedCuit] = useState<string>('');
  const [clientInfo, setClientInfo] = useState<clientData>(
    basicData || initialClientData
  );
  const location = useLocation();
  const { id: urlApplicationId } = useParams<{ id?: string }>();
  
  // Ref para rastrear si el componente cargó una solicitud existente (desde dashboard)
  // Se inicializa en false y se actualiza en useEffect para manejar carga asíncrona
  const isExistingApplicationRef = useRef<boolean>(false);

  // Detectar si es una solicitud existente (abierta desde dashboard)
  // Solo se considera existente si la URL tiene el patrón /applications/:id
  // NO se considera existente si el usuario busca un CUIT en /clientes
  useEffect(() => {
    const isExistingApplicationUrl = location.pathname.includes('/applications/');
    if (!isExistingApplicationRef.current && isExistingApplicationUrl && basicData?.id && basicData.id !== '0' && basicData.id !== '') {
      isExistingApplicationRef.current = true;
    }
  }, [basicData?.id, location.pathname]);

  // Helper para convertir ReferrerName a string (code) para enviar al backend
  const getReferrerNameCode = (referrerName: string | ReferrerName | undefined): string => {
    if (!referrerName) return '';
    if (typeof referrerName === 'string') return referrerName;
    return referrerName.code;
  };

  // Helper para convertir string a ReferrerName cuando se recibe del backend
  const getReferrerNameFromCode = (code: string | undefined): ReferrerName | string => {
    if (!code) return '';
    const referrerName = referrerNames.find(rn => rn.code === code);
    return referrerName || code; // Si no se encuentra, devolver el código como string (compatibilidad)
  };

  // Helper para obtener el label del referrer name
  const getReferrerNameLabel = (referrerName: string | ReferrerName | undefined): string => {
    if (!referrerName) return '';
    if (typeof referrerName === 'string') {
      const found = referrerNames.find(rn => rn.code === referrerName);
      return found ? found.name : referrerName;
    }
    return referrerName.name;
  };

  // Convertir referrerNames a items para DropDown (memoizado porque se usa en JSX)
  const referrerNameItems = useMemo(() => {
    return referrerNames.map((referrerName) => ({
      label: referrerName.name,
      disabled: false,
      code: referrerName.code,
    }));
  }, [referrerNames]);

  // Preparar items para el dropdown de category types (sin useMemo, directo como en CheckSection)
  const categoryTypeItems = (Array.isArray(categoryTypes) ? categoryTypes : [])?.map(ct => ({
    label: ct.name,
    disabled: false
  })) || [];

  // Helper para obtener el título del category type seleccionado
  const getSelectedCategoryType = () => {
    const categoryType = categoryTypes?.find(ct => ct.id === clientInfo.category_type_id);
    return categoryType?.name || 'Seleccionar rubro';
  };

  // Handler para cambio de category type
  const handleCategoryTypeChange = (selectedLabel: string) => {
    const categoryType = categoryTypes?.find(ct => ct.name === selectedLabel);
    if (categoryType) {
      setClientInfo((prev) => ({
        ...prev,
        category_type_id: categoryType.id,
      }));
    }
  };

  // Revalidar el formulario cuando cambie category_type_id para que el botón Continuar se active correctamente
  useEffect(() => {
    if (!isClientCategoryTypeEnabled) return;
    basicDataFormik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientInfo.category_type_id, isClientCategoryTypeEnabled]);

  const resetAndValidateForms = (basicData: any, corporateStructure: any, officer: any) => {
    basicDataFormik.resetForm({
      values: {
        ...basicData,
        date_incorporation: formatDateToPicker(basicData.date_incorporation),
        officer: officer,
      },
    });
    societaryFormik.resetForm({ values: corporateStructure });

    setTimeout(() => {
      basicDataFormik.setTouched({
        ...Object.keys(basicData).reduce(
          (acc, key) => {
            acc[key] = true;
            return acc;
          },
          {} as Record<string, boolean>
        ),
        officer: true,
      });
      societaryFormik.setTouched(
        corporateStructure.map(() => ({
          ci: true,
          first_name: true,
          ownership_interest: true,
          position: true,
          assets: true,
          value: true,
        }))
      );
      basicDataFormik.validateForm();
      societaryFormik.validateForm();
    }, 0);
  };

  useEffect(() => {
    if (basicData.cuit) {
      const hasPendingApp = basicData.id_application &&
                           basicData.id_application !== null &&
                           basicData.id_application !== undefined &&
                           basicData.id_application !== 0;

      // Si estamos editando una solicitud existente (hay urlApplicationId), no ocultar los datos
      const isEditingExistingApplication = urlApplicationId && urlApplicationId !== '0' && urlApplicationId !== '';

      // Si estamos editando una solicitud existente, siempre mostrar los datos
      if (isEditingExistingApplication && basicData.basic_data && basicData.basic_data.company_name) {
        setHasPendingApplication(false);
        setIsClientFound(true);
        setIsApplicationOnCourse(true);
        setHasClientBeenSaved(true);
        isValidatedClient(true);
        setSearchedCuit(formatCuit(basicData.cuit));
        isExistingApplicationRef.current = true;
        
        // Actualizar los formularios si no están actualizados
        if (!clientInfo.basic_data?.company_name || clientInfo.basic_data.company_name !== basicData.basic_data.company_name) {
          setClientInfo(basicData);
          basicDataFormik.setValues({
            ...basicData.basic_data,
            officer: basicData.officer,
            date_incorporation: formatDateToPicker(basicData.basic_data.date_incorporation),
          });
          societaryFormik.setValues(basicData.corporate_structure || []);
          cuitFormik.setFieldValue('cuit', formatCuit(basicData.cuit));
        }
        
        basicDataFormik.validateForm();
        societaryFormik.validateForm();
        return;
      }

      // Si hay solicitud pendiente y NO estamos editando una existente, ocultar datos
      if (hasPendingApp) {
        setHasPendingApplication(true);
        setIsClientFound(false);
        setIsApplicationOnCourse(false);
        return;
      }

      // Si no hay solicitud pendiente, mostrar los datos
      if (!hasPendingApp) {
        setHasPendingApplication(false);
        setIsClientFound(true);
        setIsApplicationOnCourse(true);
      }
      isValidatedClient(true);
      setSearchedCuit(basicData.cuit);
      basicDataFormik.validateForm();
      societaryFormik.validateForm();
      if (isExistingApplicationRef.current && basicData.id && basicData.id !== '0' && basicData.id !== '') {
        setHasClientBeenSaved(true);
      }
    }
  }, [basicData, urlApplicationId]);

  // Sincronizar el estado local con las props del padre
  useEffect(() => {
    if (basicData && basicData.cuit && basicData.basic_data) {
      // Si estamos en el paso 1 y hay una solicitud en curso, siempre actualizar los datos
      const isEditingExistingApplication = currentStep === 1 && 
        urlApplicationId && 
        urlApplicationId !== '0' && 
        urlApplicationId !== '';
      
      // Solo actualizar si los datos del padre tienen más información que el estado local
      // O si estamos editando una solicitud existente y los datos no están cargados
      const basicDataReferrerCode = getReferrerNameCode(basicData.referrer_name);
      const clientInfoReferrerCode = getReferrerNameCode(clientInfo.referrer_name);
      const hasMoreData = 
        (basicData.corporate_structure?.length || 0) > (clientInfo.corporate_structure?.length || 0) ||
        (basicData.officer && !clientInfo.officer) ||
        (basicData.referrer_name && !clientInfo.referrer_name) ||
        (basicDataReferrerCode && !clientInfoReferrerCode);
      
      const needsUpdate = hasMoreData || 
        (isEditingExistingApplication && (!clientInfo.basic_data?.company_name || !isClientFound));
      
      if (needsUpdate) {
        setClientInfo(basicData);
        // Actualizar los formularios con los datos del padre
        basicDataFormik.setValues({
          ...basicData.basic_data,
          officer: basicData.officer,
          date_incorporation: formatDateToPicker(basicData.basic_data.date_incorporation),
        });
        societaryFormik.setValues(basicData.corporate_structure || []);
        
        // Si estamos editando una solicitud existente, asegurar que los estados estén correctos
        if (isEditingExistingApplication) {
          setIsClientFound(true);
          setIsApplicationOnCourse(true);
          setHasPendingApplication(false);
          setSearchedCuit(formatCuit(basicData.cuit));
          cuitFormik.setFieldValue('cuit', formatCuit(basicData.cuit));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicData?.cuit, basicData?.corporate_structure?.length, basicData?.officer, basicData?.referrer_name, basicData?.basic_data?.company_name, currentStep, urlApplicationId]);

  // Cargar datos automáticamente cuando se navega al paso 1 desde otro paso con una solicitud en curso
  useEffect(() => {
    // Si estamos en el paso 1, hay una solicitud en curso (id en URL), y tenemos datos del cliente
    const isNavigatingToStep1 = currentStep === 1;
    const hasApplicationInUrl = urlApplicationId && urlApplicationId !== '0' && urlApplicationId !== '';
    const hasClientData = basicData?.cuit && 
      basicData?.id && 
      basicData.id !== '0' && 
      basicData.id !== '' &&
      basicData.basic_data &&
      basicData.basic_data.company_name; // Asegurar que hay datos reales
    
    // Solo cargar si venimos de otro paso o si los datos no están mostrados
    const shouldLoadData = isNavigatingToStep1 && 
      hasApplicationInUrl && 
      hasClientData &&
      (!isClientFound || !clientInfo.basic_data?.company_name);
    
    if (shouldLoadData) {
      // Verificar si es una solicitud en curso (no finalizada) o si no tenemos el estado aún
      const isApplicationInProgress = !applicationStatus || 
        (applicationStatus !== APPLICATION_STATUS.rejected.code &&
         applicationStatus !== APPLICATION_STATUS.finished.code);
      
      if (isApplicationInProgress) {
        // Cargar los datos automáticamente
        setClientInfo(basicData);
        setSearchedCuit(formatCuit(basicData.cuit));
        setIsClientFound(true);
        setIsApplicationOnCourse(true);
        setHasPendingApplication(false);
        setHasClientBeenSaved(true);
        isValidatedClient(true);
        isExistingApplicationRef.current = true;
        
        // Actualizar los formularios con los datos
        basicDataFormik.setValues({
          ...basicData.basic_data,
          officer: basicData.officer,
          date_incorporation: formatDateToPicker(basicData.basic_data.date_incorporation),
        });
        societaryFormik.setValues(basicData.corporate_structure || []);
        
        // Actualizar el CUIT en el formulario de búsqueda
        cuitFormik.setFieldValue('cuit', formatCuit(basicData.cuit));
        
        // Validar los formularios
        setTimeout(() => {
          basicDataFormik.validateForm();
          societaryFormik.validateForm();
        }, 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, urlApplicationId, basicData?.cuit, basicData?.id, basicData?.basic_data?.company_name, applicationStatus]);

  // Detectar navegación entre pasos y mantener el estado
  useEffect(() => {
    if (currentStep === 1 && clientInfo.cuit) {
      // Logs de debugging removidos - el fix está funcionando
      
      // Solo sincronizar si el estado local tiene más datos que el padre
      const clientInfoReferrerCode = getReferrerNameCode(clientInfo.referrer_name);
      const basicDataReferrerCode = getReferrerNameCode(basicData?.referrer_name);
      const hasLocalChanges = 
        societaryFormik.values.length > (basicData?.corporate_structure?.length || 0) ||
        (basicDataFormik.values.officer && !basicData?.officer) ||
        (clientInfoReferrerCode && !basicDataReferrerCode);
      
      if (hasLocalChanges) {
        const updatedClientInfo = {
          ...clientInfo,
          basic_data: basicDataFormik.values,
          corporate_structure: societaryFormik.values,
          officer: basicDataFormik.values.officer,
          referrer_name: clientInfo.referrer_name,
        };
        if (onDataChange) {
          onDataChange(updatedClientInfo);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  useEffect(() => {
    if (
      location.pathname === '/clientes' &&
      currentStep === 1 &&
      previousStep === 1
    ) {
      setClientInfo(initialClientData);
      setHasClientBeenSaved(false); // Resetear cuando se resetea el cliente
      isExistingApplicationRef.current = false; // Resetear ref para permitir nueva búsqueda
      setSearchedCuit(''); // Resetear el CUIT buscado
      setIsClientFound(false);
      setIsApplicationOnCourse(false);
      setIsSearching(false);
      setHasPendingApplication(false);
      setIsCuitValid(true);
      setValidatedBasicData(true);
      setErrorMsg('');
      resetAndValidateForms(
        { ...initialClientData.basic_data, date_incorporation: '' },
        initialClientData.corporate_structure,
        initialClientData.officer
      );
      cuitFormik.resetForm({ values: { cuit: '' } });
    }
  }, [location.pathname, currentStep, previousStep]);

  const formatDateToPicker = (dateStr: string) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${year}/${day}/${month}`;
  };

  const loadClientData = (response: any, cuitValue: string, cuitFormatted: string) => {
    const basicDataWithLegalAddress = {
      ...response.data.basic_data,
      legal_company_address: response.data.basic_data.company_address || '',
      legal_city: response.data.basic_data.city || '',
      legal_state: response.data.basic_data.state || '',
    };
    const updatedResponseData = {
      ...response.data,
      basic_data: basicDataWithLegalAddress,
      category_type_id: response.data.category_type?.id || response.data.category_type_id,
      verified: true,
    };
    setClientInfo(updatedResponseData);
    setSearchedCuit(cuitFormatted);

    if (onDataChange) {
      onDataChange(updatedResponseData);
    }
    setIsCuitValid(true);
    isValidatedClient(true);
    setErrorMsg('');

    resetAndValidateForms(
      basicDataWithLegalAddress,
      response.data.corporate_structure || [],
      response.data.officer || ''
    );
    setValidatedBasicData(true);
    setIsClientFound(true);
    setIsSearching(false);
  };

  const cuitFormik = useFormik({
    initialValues: { cuit: clientInfo.cuit },
    onSubmit: (values) => {
      if (!values.cuit || values.cuit.length === 0) {
        setErrorMsg('Debes ingresar un CUIT');
        return;
      }
      if (values.cuit.length < 13) {
        setErrorMsg('El CUIT ingresado tiene un formato incorrecto');
        return;
      }
      {
        setErrorMsg('');
        setIsSearching(true);
        getClientInfo(values.cuit.replace(/-/g, '')).then((response) => {
          if (response.success && 'data' in response) {
            const cuitValue = values.cuit.replace(/-/g, '');

            const applicationId = response.data?.id_application;
            const responseId = response.data?.id;
            const valueToSend = applicationId || responseId;

            const hasValidApplicationId = valueToSend != null &&
                                         valueToSend !== undefined &&
                                         valueToSend !== '' &&
                                         String(valueToSend).trim() !== '' &&
                                         String(valueToSend).trim() !== '-1' &&
                                         valueToSend !== 0;

            if (hasValidApplicationId) {
              getApplicationsByCuit(cuitValue).then((appsResponse) => {
                if (appsResponse.success && 'data' in appsResponse) {
                  const applications = appsResponse.data?.content || [];

                  let hasActiveApplication = false;
                  let isEditingCurrentApplication = false;

                  // Verificar si estamos editando la solicitud actual
                  if (urlApplicationId) {
                    const currentAppId = String(urlApplicationId);
                    isEditingCurrentApplication = applications.some((app: any) => 
                      String(app.id) === currentAppId
                    );
                  }

                  applications.forEach((app: any) => {
                    if (app.status?.id) {
                      const appStatus = Number(app.status.id);
                      const isFinalStatus = appStatus === APPLICATION_STATUS.rejected.code ||
                                           appStatus === APPLICATION_STATUS.finished.code;

                      if (!isFinalStatus) {
                        hasActiveApplication = true;
                      }
                    } else {
                      hasActiveApplication = true;
                    }
                  });

                  // Si estamos editando la solicitud actual, permitir cargar los datos
                  // Solo bloquear si estamos creando una nueva solicitud y hay una activa
                  if (hasActiveApplication && !isEditingCurrentApplication) {
                    setHasPendingApplication(true);
                    setIsClientFound(false);
                    setIsApplicationOnCourse(false);

                    onSendStatus(valueToSend);

                    setClientInfo({
                      ...initialClientData,
                      cuit: cuitValue
                    });

                    basicDataFormik.resetForm({ values: { ...initialClientData.basic_data, officer: '' } });
                    societaryFormik.resetForm({ values: [] });

                    setIsSearching(false);
                    setErrorMsg('');

                    return;
                  } else {
                    setHasPendingApplication(false);
                    // Si estamos editando la solicitud actual, cargar datos sin mensaje
                    // Si no hay solicitudes activas, mostrar mensaje informativo
                    if (!isEditingCurrentApplication) {
                      useToast.success({
                        title: 'Ya existe el cliente. No tiene solicitudes vigentes.',
                        id: 'save-toast',
                        description: '',
                        closeButton: true,
                        autoClose: 5000,
                      });
                    }
                    loadClientData(response, cuitValue, values.cuit);
                  }
                } else {
                  setHasPendingApplication(true);
                  setIsClientFound(false);
                  setIsApplicationOnCourse(false);
                  onSendStatus(valueToSend);
                  setClientInfo({
                    ...initialClientData,
                    cuit: cuitValue
                  });
                  basicDataFormik.resetForm({ values: { ...initialClientData.basic_data, officer: '' } });
                  societaryFormik.resetForm({ values: [] });
                  setIsSearching(false);
                  setErrorMsg('');
                }
              }).catch(() => {
                setHasPendingApplication(true);
                setIsClientFound(false);
                setIsApplicationOnCourse(false);
                setIsSearching(false);
                setErrorMsg('Error al verificar el estado de la solicitud');
              });
            } else {
              setHasPendingApplication(false);
              useToast.info({
                title: 'Ya existe el cliente. No tiene solicitudes vigentes.',
                id: 'client-exists-toast',
                description: '',
                closeButton: true,
                autoClose: 5000,
              });
              loadClientData(response, cuitValue, values.cuit);
            }
          } else {
            setIsCuitValid(false);
            isValidatedClient(false);
            setValidatedBasicData(false);

            // Crear un nuevo clientData con basic_data vacío pero con el nuevo CUIT
            const newClientData = {
              ...initialClientData,
              cuit: values.cuit.replace(/-/g, ''),
              verified: false,
            };
            
            // Resetear el formulario con datos vacíos
            resetAndValidateForms(
              initialClientData.basic_data,
              [],
              ''
            );
            
            setClientInfo(newClientData);
            setSearchedCuit(values.cuit);
            
            // Actualizar el CUIT en el padre con los datos limpios
            if (onDataChange) {
              onDataChange(newClientData);
            }
            
            setIsClientFound(true);
          }
          setIsSearching(false);
        });
      }
    },
  });

  const validateBasicData = (values: basicData) => {
    const errors: any = {};
    if (!values.company_name) {
      errors.company_name = 'El campo Razón social es obligatorio';
    }
    if (!values.company_address) {
      errors.company_address = 'El campo Domicilio fiscal es obligatorio';
    }
    if (!values.state) {
      errors.state = 'El campo Provincia es obligatorio';
    }
    if (!values.city) {
      errors.city = 'El campo Localidad es obligatorio';
    }
    if (!values.email) {
      errors.email = 'El campo email es obligatorio';
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)
    ) {
      errors.email = 'Formato de email inválido';
    }
    if (!values.legal_entity) {
      errors.legal_entity = 'El campo Personería es obligatorio';
    }
    if (!values.activity) {
      errors.activity = 'El campo Actividad es obligatorio';
    }
    if (!values.iva_status) {
      errors.iva_status = 'El campo Condición frente al IVA es obligatorio';
    }
    if (isClientCategoryTypeEnabled && !clientInfo.category_type_id) {
      errors.category_type_id = 'El campo Rubro es obligatorio';
    }
    if (!values.date_incorporation) {
      errors.date_incorporation =
        'El campo Fecha de constitución es obligatorio';
    }
    return errors;
  };

  const basicDataFormik = useFormik({
    initialValues: { ...clientInfo.basic_data, officer: clientInfo.officer },
    validate: validateBasicData,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: (values) => {},
  });

  const validateCorporateStructure = (values: corporateStructureData[]) => {
    const errors: any[] = [];
    let totalOwnershipInterest = 0;

    values.forEach((partner) => {
      totalOwnershipInterest += Number(partner.ownership_interest);
    });

    values.forEach((partner, index) => {
      const partnerErrors: any = {};
      if (!partner.ci) {
        partnerErrors.ci = 'El campo Documento es obligatorio';
      }
      if (!partner.first_name) {
        partnerErrors.first_name = 'El campo Nombre es obligatorio';
      }
      if (totalOwnershipInterest > 100) {
        partnerErrors.ownership_interest =
          'La suma de las participaciones no debe superar el 100%';
      }
      if (Object.keys(partnerErrors).length > 0) {
        errors[index] = partnerErrors;
      }
    });
    return errors;
  };

  const societaryFormik = useFormik({
    initialValues: clientInfo.corporate_structure || [
      {
        id: 0,
        person_id: 0,
        ci: '',
        first_name: '',
        last_name: '',
        ownership_interest: '0.00',
        position: '',
        assets: '',
        assets_file_path: undefined,
        value: 0.0,
      },
    ],
    validate: validateCorporateStructure,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: (values) => {},
  });

  const addCorporateStructure = () => {
    const newPartner = {
      id: 0,
      person_id: 0,
      ci: '',
      first_name: '',
      last_name: '',
      ownership_interest: '0.00',
      position: '',
      assets: '',
      assets_file_path: undefined,
      value: 0.0,
    };

    const updatedValues = [...societaryFormik.values, newPartner];
    societaryFormik.setValues(updatedValues);

    const updatedClientInfo = {
      ...clientInfo,
      corporate_structure: updatedValues,
    };
    
    setClientInfo(updatedClientInfo);
    // Logs de debugging removidos - el fix está funcionando
    // Notificar al padre sobre los cambios
    if (onDataChange) {
      onDataChange(updatedClientInfo);
    }
  };

  const removeCorporateMember = (index: number) => {
    const updatedValues = societaryFormik.values.filter((_, i) => i !== index);

    societaryFormik.setValues(updatedValues);

    const updatedClientInfo = {
      ...clientInfo,
      corporate_structure: updatedValues,
    };
    
    setClientInfo(updatedClientInfo);
    // Logs de debugging removidos - el fix está funcionando
    // Notificar al padre sobre los cambios
    if (onDataChange) {
      onDataChange(updatedClientInfo);
    }
  };

  const handleSave = (shouldContinue: boolean) => {
    const { officer, ...basicDataWithoutOfficer } = basicDataFormik.values;

    // Construir el objeto category_type completo si existe category_type_id
    const categoryType = clientInfo.category_type_id 
      ? categoryTypes?.find(ct => ct.id === clientInfo.category_type_id)
      : undefined;

    const updatedClientInfo = {
      ...clientInfo,
      basic_data: basicDataWithoutOfficer,
      corporate_structure: societaryFormik.values,
      officer: basicDataFormik.values.officer,
      referrer_name: getReferrerNameCode(clientInfo.referrer_name), // Convertir a código para guardar
      category_type: categoryType, // Agregar el objeto completo
    };
    
    setClientInfo(updatedClientInfo);
    // Logs de debugging removidos - el fix está funcionando
    // Notificar al padre sobre los cambios
    if (onDataChange) {
      onDataChange(updatedClientInfo);
    }
    onSave({ clientInfo: updatedClientInfo, shouldContinue });
  };

  const isApplicationEditable = () => {
    return !isRejected && !isFinished && !isApproved && !isInCommittee;
  };

  const handleOnClickActivity = (e: activity) => {
    basicDataFormik.setFieldValue(
      'activity',
      economicActivities.find((item) => item.code === e.code)?.code
    );
  };

  const handleOnClickDateIcorporation = (e: any) => {
    const date = e || initialClientData.basic_data.date_incorporation;
    basicDataFormik.setFieldValue('date_incorporation', date);
  };

  const handleUploadAssets = async (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>,
    socio: corporateStructureData
  ) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    // Validar tamaño del archivo (20MB máximo)
    if (file.size >= MAX_FILE_SIZE) {
      useToast.error({
        title: 'El tamaño del archivo no puede ser mayor de 20MB',
        id: 'save-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
      // Limpiar el input de archivo
      event.target.value = '';
      return;
    }

    // Obtener el CI actualizado del formulario en lugar del objeto socio
    const currentCi = societaryFormik.values[index]?.ci;

    // Validar que el CI exista antes de permitir la carga
    if (!currentCi || currentCi.trim() === '') {
      setAssetUploadError({
        ...assetUploadError,
        [index]: 'Debe ingresar el documento del socio antes de cargar la manifestación de bienes',
      });
      // Limpiar el input de archivo
      event.target.value = '';
      return;
    }

    // Limpiar error previo si existe
    setAssetUploadError({
      ...assetUploadError,
      [index]: '',
    });

    setUploadingAssetIndex(index);

    try {
      // 0. Primero guardar el cliente para asegurar que existe en la BD (solo la primera vez)
      // Estrategia simple: usar SOLO el ID del cliente para verificar si existe en la BD
      // - Si el cliente tiene ID válido (existe en BD) → NO llamar al endpoint
      // - Si el cliente NO tiene ID válido (es nuevo) → llamar al endpoint
      
      // Verificar si el cliente existe en la BD usando SOLO el ID
      // Si el ID no está disponible, verificar id_application como indicador de que la carpeta ya existe
      const idToCheck = basicData?.id || clientInfo.id;
      const idValue = idToCheck ? String(idToCheck) : '';
      // El cliente existe en la BD si tiene un ID válido (no vacío, no '0', no '-1')
      const hasValidClientId = idValue !== '' && idValue !== '0' && idValue !== '-1' && idValue.trim() !== '';
      
      // Si el ID no está disponible, verificar si tiene id_application (carpeta ya guardada)
      const hasApplicationId = basicData?.id_application !== undefined && basicData?.id_application !== null && basicData?.id_application !== 0;
      
      // El cliente existe si tiene ID válido O si tiene id_application (carpeta en revisión)
      const clientExistsInDb = hasValidClientId || hasApplicationId;
      
      // Guardar SOLO si el cliente NO existe en la BD
      const shouldSave = !clientExistsInDb;
      
      if (shouldSave) {
        const { officer, ...basicDataWithoutOfficer } = basicDataFormik.values;
        const clientDataToSave = {
          ...clientInfo,
          basic_data: basicDataWithoutOfficer,
          corporate_structure: societaryFormik.values,
          officer: basicDataFormik.values.officer,
          referrer_name: getReferrerNameCode(clientInfo.referrer_name), // Convertir a código para guardar
        };

        await saveClientInfo(clientDataToSave, clientInfo.cuit, 0);
        setHasClientBeenSaved(true);
      }

      // 1. Analizar con IA (flujo actual)
      const formDataForAI = new FormData();
      formDataForAI.append('file', file);
      const aiResponse = await loadAssets(formDataForAI, index);

      // 2. Guardar archivo físico
      const formDataForStorage = new FormData();
      formDataForStorage.append('file', file);
      formDataForStorage.append('type', 'ASSET_DECLARATION');
      formDataForStorage.append(
        'fileName',
        `asset-${currentCi}-${Date.now()}`
      );
      formDataForStorage.append('isBasicData', 'false');

      const storageResponse = await saveClientDocument(
        clientInfo.cuit,
        formDataForStorage
      );

      // Verificar si el guardado del archivo falló
      if (!storageResponse.success) {
        let errorMessage = 'Error al cargar el archivo';
        
        // Verificar si hay un mensaje válido en la respuesta
        if ('message' in storageResponse && storageResponse.message) {
          const message = storageResponse.message;
          // Si el mensaje es un string válido y no es "[object Object]", usarlo
          if (typeof message === 'string' && message.trim() !== '' && message !== '[object Object]') {
            errorMessage = message;
          }
        }
        
        // Si es error 413 (Payload Too Large), usar mensaje específico
        if ('status' in storageResponse && storageResponse.status === 413) {
          errorMessage = 'El archivo es demasiado grande. Por favor, intente con un archivo más pequeño.';
        }
        
        useToast.error({
          title: errorMessage,
          id: 'save-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
        setAssetUploadError({
          ...assetUploadError,
          [index]: errorMessage,
        });
        return;
      }

      // 3. Actualizar ambos valores
      if (aiResponse.success && storageResponse.success) {
        societaryFormik.setFieldValue(
          `[${index}].assets`,
          aiResponse.data.assets
        );
        societaryFormik.setFieldValue(
          `[${index}].assets_file_path`,
          storageResponse.data
        );
        societaryFormik.setFieldValue(`[${index}].value`, aiResponse.data.value);

        socio.assets = aiResponse.data.assets;
        socio.assets_file_path = storageResponse.data;
        socio.value = aiResponse.data.value;
      }
    } catch (error: unknown) {
      console.error('Error uploading asset:', error);
      let errorMessage = 'Error al subir el archivo';
      
      if (error && typeof error === 'object') {
        // Verificar si es un error 413 (Payload Too Large)
        if ('response' in error && error.response && typeof error.response === 'object') {
          const response = error.response as any;
          if (response.status === 413) {
            errorMessage = 'El archivo es demasiado grande. Por favor, intente con un archivo más pequeño.';
          }
        }
      }
      
      useToast.error({
        title: errorMessage,
        id: 'save-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
      setAssetUploadError({
        ...assetUploadError,
        [index]: errorMessage,
      });
    } finally {
      setUploadingAssetIndex(null);
    }
  };

  const handleViewAsset = (fileName: string) => {
    downloadClientDocument(clientInfo.cuit, fileName).then((response) => {
      if (response.success) {
        const fileBlob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(fileBlob);
        window.open(url, '_blank');
      }
    });
  };

  const rendererManifestacionBienesFile = (
    index: number,
    socio: corporateStructureData
  ) => {
    const assets = societaryFormik.values[index]?.assets;
    const isUploading = uploadingAssetIndex === index;

    // Si ya tiene assets, mostrar input editable
    if (assets) {
      return (
        <Input
          label={index === 0 ? 'Manifestación de bienes' : ''}
          id='corporate_structure-input-assets'
          value={assets}
          onChange={(e) =>
            societaryFormik.setFieldValue(`[${index}].assets`, e.target.value)
          }
          disabled={!isApplicationEditable()}
        />
      );
    }

    // Si no tiene assets, mostrar input de archivo con spinner centrado cuando está cargando
    // Usar el valor actualizado del formulario en lugar del objeto socio
    const currentCi = societaryFormik.values[index]?.ci;
    const hasCi = currentCi && currentCi.trim() !== '';
    const errorMessage = assetUploadError[index];

    return (
      <div style={{ position: 'relative', width: '100%' }}>
        <div style={{ opacity: isUploading ? 0 : 1, pointerEvents: isUploading ? 'none' : 'auto' }}>
          <Input
            id={`file-input-asset-${index}`}
            label={index === 0 ? 'Manifestación de bienes' : ''}
            type='file'
            size='md'
            icons={!isUploading ? { iconRight: 'upload_outline' } : undefined}
            onChange={(event) => {
              handleUploadAssets(index, event, socio);
            }}
            accept='.pdf'
            disabled={!isApplicationEditable() || isUploading || !hasCi}
          />
          {errorMessage && (
            <Span variant='danger' type='2'>
              {errorMessage}
            </Span>
          )}
          {!hasCi && !errorMessage && (
            <Span variant='danger' type='2'>
              Debe ingresar el documento del socio antes de cargar la manifestación de bienes
            </Span>
          )}
        </div>
        {isUploading && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
              zIndex: 10,
            }}
          >
            <Spinner size='md' variant='primary' />
          </div>
        )}
      </div>
    );
  };

  const renderComposicionSocietaria = () => {
    return clientInfo.corporate_structure?.map(
      (socio: corporateStructureData, index) => (
        <Card
          key={socio.id}
          display='flex'
          gap='12px'
          margin={{ bottom: '2' }}
        >
            <Card width='-webkit-fill-available'>
              <Input
                label={index === 0 ? 'Documento' : ''}
                id='corporate_structure-input-ci'
                value={societaryFormik.values[index]?.ci}
                onlyNumbers
                onChange={(e) => {
                  societaryFormik.setFieldValue(`[${index}].ci`, e.target.value);
                  // Limpiar error de carga de asset si existe cuando se ingresa el CI
                  if (assetUploadError[index]) {
                    setAssetUploadError({
                      ...assetUploadError,
                      [index]: '',
                    });
                  }
                }}
                disabled={!isApplicationEditable()}
              />
              <Span variant='danger' type='2'>
                {societaryFormik.errors[index]?.ci}
              </Span>
            </Card>
            <Card width='-webkit-fill-available'>
              <Input
                label={index === 0 ? 'Nombre' : ''}
                id='corporate_structure-input-name'
                value={societaryFormik.values[index]?.first_name}
                type='text'
                onChange={(e) =>
                  societaryFormik.setFieldValue(
                    `[${index}].first_name`,
                    e.target.value.replace(/[0-9]/g, '')
                  )
                }
                disabled={!isApplicationEditable()}
              />
              <Span variant='danger' type='2'>
                {societaryFormik.errors[index]?.first_name}
              </Span>
            </Card>
            <Card width='50%'>
              <Input
                label={index === 0 ? 'Participación' : ''}
                id='corporate_structure-input-ownership_interest'
                inputStatus={
                  societaryFormik.errors[index]?.ownership_interest
                    ? 'error'
                    : 'normal'
                }
                value={societaryFormik.values[index]?.ownership_interest}
                onChange={(e) =>
                  societaryFormik.setFieldValue(
                    `[${index}].ownership_interest`,
                    parseFloat(e.target.value) || 0
                  )
                }
                disabled={!isApplicationEditable()}
              />
            </Card>

            <Input
              label={index === 0 ? 'Cargo' : ''}
              id='corporate_structure-input-position'
              value={societaryFormik.values[index]?.position}
              onChange={(e) =>
                societaryFormik.setFieldValue(
                  `[${index}].position`,
                  e.target.value
                )
              }
              disabled={!isApplicationEditable()}
            />
            <Card width='-webkit-fill-available'>
              {rendererManifestacionBienesFile(index, socio)}
              {/*  <Input
                      label={index === 0 ? 'Manifestación de bienes' : ''}
                      id='corporate_structure-input-assets'
                      value={societaryFormik.values[index]?.assets}
                      onChange={(e) =>
                        societaryFormik.setFieldValue(
                          `[${index}].assets`,
                          e.target.value
                        )
                      }
                      disabled={!isApplicationEditable()}
                    /> */}
            </Card>
            <NumericFormat
              label={index === 0 ? 'Valor' : ''}
              id='corporate_structure-input-value'
              value={societaryFormik.values[index]?.value ?? ''}
              thousandSeparator='.'
              decimalSeparator=','
              onValueChange={(values) =>
                societaryFormik.setFieldValue(
                  `[${index}].value`,
                  values.value === '' ? '' : values.floatValue
                )
              }
              customInput={Input}
              disabled={!isApplicationEditable()}
              decimalScale={2}
              allowNegative={false}
            />
            <Card
              display='flex'
              gap='8px'
              alignItems='flex-start'
              margin={index === 0 ? { top: '4' } : undefined}
            >
              {societaryFormik.values[index]?.assets_file_path && (
                <Button
                  variant='secondary'
                  size='md'
                  onClick={() =>
                    handleViewAsset(
                      societaryFormik.values[index]?.assets_file_path || ''
                    )
                  }
                  disabled={!isApplicationEditable()}
                >
                  <Icon
                    name='view_outline'
                    variant='neutral'
                    height={44}
                    cursor='pointer'
                  />
                </Button>
              )}
              <Button
                size='md'
                variant='secondary'
                disabled={
                  (index === 0 &&
                    clientInfo.corporate_structure.length === 1) ||
                  !isApplicationEditable()
                }
                onClick={() => removeCorporateMember(index)}
              >
                <Icon name='delete_outline' variant='neutral' height={44} cursor='pointer' />
              </Button>
            </Card>
          </Card>
        )
    );
  };

  return (
    <Card>
      <Card display='grid' gap='8px' margin={{ bottom: '4' }}>
        <P
          type='1'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
        >
          Datos del cliente
        </P>
        <P type='1' withMargin={false} variant='neutral' modifier='regular'>
          Escribí el CUIT del cliente para poder comenzar la carga al sistema.
        </P>
      </Card>
      <form onSubmit={cuitFormik.handleSubmit}>
        <Card display='flex' width='30vw' gap='32px' alignItems='center'>
          <Input
            id='client-input-cuit'
            placeholder='Ingresá un CUIT'
            inputStatus={errorMsg !== '' ? 'error' : 'normal'}
            size='md'
            icons={{ iconLeft: 'search_outline' }}
            autocomplete='off'
            maxLength={13}
            onChange={(e) => {
              const formattedValue = formatCuit(e.target.value);
              cuitFormik.setFieldValue('cuit', formattedValue);
              
              // Si el CUIT cambió respecto al buscado, resetear estados para permitir nueva búsqueda
              const rawNewCuit = formattedValue.replace(/-/g, '');
              const rawSearchedCuit = searchedCuit.replace(/-/g, '');
              if (rawNewCuit !== rawSearchedCuit && !hasClientBeenSaved) {
                setIsApplicationOnCourse(false);
                setIsCuitValid(true);
                setValidatedBasicData(true);
              }
            }}
            onPaste={(e) => {
              e.preventDefault();
              const pasted = e.clipboardData.getData('Text').replace(/\D/g, '');
              let formatted = pasted;
              if (pasted.length === 11) {
                formatted = `${pasted.slice(0, 2)}-${pasted.slice(2, 10)}-${pasted.slice(10, 11)}`;
              }
              cuitFormik.setFieldValue('cuit', formatted);
              
              // Si el CUIT cambió respecto al buscado, resetear estados para permitir nueva búsqueda
              const rawNewCuit = formatted.replace(/-/g, '');
              const rawSearchedCuit = searchedCuit.replace(/-/g, '');
              if (rawNewCuit !== rawSearchedCuit && !hasClientBeenSaved) {
                setIsApplicationOnCourse(false);
                setIsCuitValid(true);
                setValidatedBasicData(true);
              }
            }}
            message={errorMsg}
            value={cuitFormik.values.cuit}
            disabled={hasClientBeenSaved}
          />
          <Button
            variant='tertiary-filled'
            size='sm'
            style={{ minWidth: '80px', justifyContent: 'center' }}
            disabled={
              hasClientBeenSaved || 
              isApplicationOnCourse || 
              isSearching || 
              !cuitFormik.values.cuit || 
              cuitFormik.values.cuit.length < 13
            }
            onClick={cuitFormik.handleSubmit}
          >
            {isSearching ? <Spinner size='sm' variant='neutral' /> : 'Buscar'}
          </Button>
        </Card>
      </form>
      <Card display='grid' gap='8px' margin={{ bottom: '4', top: '4' }}>
        <P
          type='1'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
        >
          Datos generales
        </P>
        <P type='1' withMargin={false} variant='neutral' modifier='regular'>
          Revisá que los datos del CUIT sean correctos. Podés modificarlos.
        </P>
      </Card>
      {(isClientFound || isApplicationOnCourse) && !hasPendingApplication ? (
        <Card margin={{ top: '4' }}>
          <form onSubmit={basicDataFormik.handleSubmit}>
            <Card>
              <Card display='flex' gap='24px' alignItems='flex-start'>
                <Card width='35vw'>
                  <Input
                    label='Razón social *'
                    id='client-input-company_name'
                    value={basicDataFormik.values.company_name}
                    maxLength={300}
                    type='text'
                    onChange={(e) =>
                      basicDataFormik.setFieldValue(
                        'company_name',
                        e.target.value
                      )
                    }
                    disabled={false}
                  />
                  <Span variant='danger' type='2'>
                    {basicDataFormik.errors.company_name}
                  </Span>
                </Card>
                <Card width='25vw'>
                  <DropDown
                    id='client-legal_entity-dropdown'
                    label='Personería'
                    items={legalEntities}
                    size='md'
                    title={basicDataFormik.values.legal_entity}
                    onChange={(e) =>
                      basicDataFormik.setFieldValue(
                        'legal_entity',
                        legalEntities.find((item) => item.label === e)?.label
                      )
                    }
                    disabled={!isApplicationEditable()}
                  ></DropDown>
                  <Span variant='danger' type='2'>
                    {basicDataFormik.errors.legal_entity}
                  </Span>
                </Card>
                <Card width='25vw'>
                  <P withMargin={false} variant='neutral-1' type='4'>
                    Fecha de constitución
                  </P>
                  <Card margin={{ top: '3', right: '3' }}>
                    <DatePicker
                      disabled={false}
                      defaultValue={basicDataFormik.values.date_incorporation}
                      onChange={handleOnClickDateIcorporation}
                    />
                  </Card>
                  <Span variant='danger' type='2'>
                    {basicDataFormik.errors.date_incorporation}
                  </Span>
                </Card>
              </Card>
              <Card display='flex' margin={{ top: '3' }} gap='24px' alignItems='flex-start'>
                <Card width='30vw'>
                  <SearchableSelect
                    disabled={false}
                    id='client-search-activity'
                    label='Actividad económica'
                    options={economicActivities}
                    value={
                      economicActivities.find(
                        (item) =>
                          item.code ===
                          parseInt(basicDataFormik.values.activity)
                      )?.label
                    }
                    onChange={(e) =>
                      basicDataFormik.setFieldValue(
                        'activity',
                        economicActivities.find((item) => item.code === e)?.code
                      )
                    }
                    onClick={handleOnClickActivity}
                  ></SearchableSelect>
                  <Span variant='danger' type='2'>
                    {basicDataFormik.errors.activity}
                  </Span>
                </Card>
                <Card width='30vw'>
                  <DropDown
                    id='client-iva_status-dropdown'
                    label='Condición frente al IVA'
                    items={ivaStatuses}
                    size='md'
                    title={
                      ivaStatuses.find(
                        (item) =>
                          item.code === basicDataFormik.values.iva_status
                      )?.label
                    }
                    onChange={(e) =>
                      basicDataFormik.setFieldValue(
                        'iva_status',
                        ivaStatuses.find((item) => item.label === e)?.code
                      )
                    }
                    disabled={!isApplicationEditable()}
                  ></DropDown>
                  <Span variant='danger' type='2'>
                    {basicDataFormik.errors.iva_status}
                  </Span>
                </Card>
                {isClientCategoryTypeEnabled && (
                  <Card>
                    <Card width='30vw'>
                      <DropDown
                        id='client-category_type-dropdown'
                        label='Rubro *'
                        title={getSelectedCategoryType()}
                        items={categoryTypeItems}
                        onChange={handleCategoryTypeChange}
                        size='md'
                        disabled={!isApplicationEditable() || isLoadingCategoryTypes}
                      />
                      <Span variant='danger' type='2'>
                        {basicDataFormik.touched.category_type_id && (basicDataFormik.errors as any).category_type_id}
                      </Span>
                    </Card>
                  </Card>
                )} 
                {!isClientCategoryTypeEnabled && (
                  <Card display='flex' gap='24px' alignItems='flex-start'>
                    <Card width='20vw' display='grid' gap='8px'>
                      <Card display='flex' gap='8px' alignItems='flex-end'>
                        <Card width='-webkit-fill-available'>
                          <DropDown
                            id='referrer-name-dropdown'
                            label='Referido'
                            items={referrerNameItems}
                            size='md'
                            title={getReferrerNameLabel(clientInfo.referrer_name) || 'Seleccionar referido'}
                            onChange={(selectedName) => {
                              const selectedReferrer = referrerNames.find(rn => rn.name === selectedName);
                              setClientInfo((prev) => ({
                                ...prev,
                                referrer_name: selectedReferrer || selectedName,
                              }));
                            }}
                            searchable={true}
                            searchPlaceholder='Buscar productor...'
                            disabled={!isApplicationEditable() || isLoadingReferrerNames}
                          />
                        </Card>
                        {hasPermission && (
                          <Button
                            variant='secondary'
                            size='md'
                            iconAlign='center'
                            onClick={() => setIsReferrersModalOpen(true)}
                            disabled={!isApplicationEditable() || isLoadingReferrerNames}
                            style={{
                              width: '52px',
                              height: '52px',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '52px',
                              maxHeight: '52px',
                              flexShrink: 0,
                              marginBottom: '0',
                            }}
                            title='Gestionar referidos'
                          >
                            <Icon 
                              name='settings_outline' 
                              variant='neutral' 
                              modifier='strong'
                              width={24} 
                              height={24}
                              cursor='pointer'
                            />
                          </Button>
                        )}
                      </Card>
                      <ReferrersManagementModal
                        isOpen={isReferrersModalOpen}
                        onClose={() => {
                          setIsReferrersModalOpen(false);
                          // Refrescar la lista de referidos cuando se cierra el modal
                          refreshReferrerNames();
                        }}
                        onReferrerUpdated={() => {
                          // Refrescar la lista cuando se actualiza un referido
                          refreshReferrerNames();
                        }}
                      />
                    </Card>
                    <Card width='15vw'>
                      <DropDown
                        id='officers-dropdown'
                        label='Oficial'
                        items={officers}
                        size='md'
                        title={
                          officers.find(
                            (item) => item.code === basicDataFormik.values.officer
                          )?.label
                        }
                        onChange={(e) =>
                          basicDataFormik.setFieldValue(
                            'officer',
                            officers.find((item) => item.label === e)?.code
                          )
                        }
                        disabled={!isApplicationEditable() || isLoadingOfficers}
                      ></DropDown>
                    </Card>
                  </Card>
                )}
              </Card>
              {isClientCategoryTypeEnabled && (
                  <Card display='flex' margin={{ top: '3' }} gap='24px' alignItems='flex-start'>
                    <Card width='50vw' display='grid' gap='8px'>
                      <Card display='flex' gap='8px' alignItems='flex-end'>
                        <Card width='-webkit-fill-available'>
                          <DropDown
                            id='referrer-name-dropdown'
                            label='Referido'
                            items={referrerNameItems}
                            size='md'
                            title={getReferrerNameLabel(clientInfo.referrer_name) || 'Seleccionar referido'}
                            onChange={(selectedName) => {
                              const selectedReferrer = referrerNames.find(rn => rn.name === selectedName);
                              setClientInfo((prev) => ({
                                ...prev,
                                referrer_name: selectedReferrer || selectedName,
                              }));
                            }}
                            searchable={true}
                            searchPlaceholder='Buscar productor...'
                            disabled={!isApplicationEditable() || isLoadingReferrerNames}
                          />
                        </Card>
                        {hasPermission && (
                          <Button
                            variant='secondary'
                            size='md'
                            iconAlign='center'
                            onClick={() => setIsReferrersModalOpen(true)}
                            disabled={!isApplicationEditable() || isLoadingReferrerNames}
                            style={{
                              width: '52px',
                              height: '52px',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '52px',
                              maxHeight: '52px',
                              flexShrink: 0,
                              marginBottom: '0',
                            }}
                            title='Gestionar referidos'
                          >
                            <Icon 
                              name='settings_outline' 
                              variant='neutral' 
                              modifier='strong'
                              width={24} 
                              height={24}
                              cursor='pointer'
                            />
                          </Button>
                        )}
                      </Card>
                      <ReferrersManagementModal
                        isOpen={isReferrersModalOpen}
                        onClose={() => {
                          setIsReferrersModalOpen(false);
                          // Refrescar la lista de referidos cuando se cierra el modal
                          refreshReferrerNames();
                        }}
                        onReferrerUpdated={() => {
                          // Refrescar la lista cuando se actualiza un referido
                          refreshReferrerNames();
                        }}
                      />
                    </Card>
                    <Card width='50vw'>
                      <DropDown
                        id='officers-dropdown'
                        label='Oficial'
                        items={officers}
                        size='md'
                        title={
                          officers.find(
                            (item) => item.code === basicDataFormik.values.officer
                          )?.label
                        }
                        onChange={(e) =>
                          basicDataFormik.setFieldValue(
                            'officer',
                            officers.find((item) => item.label === e)?.code
                          )
                        }
                        disabled={!isApplicationEditable() || isLoadingOfficers}
                      ></DropDown>
                    </Card>
                  </Card>
                )}
              <Card display='flex' margin={{ top: '3' }} gap='24px' alignItems='flex-start'>
                <Card width='30vw'>
                  <Input
                    label='Domicilio fiscal'
                    id='client-input-company_address'
                    value={basicDataFormik.values.company_address}
                    maxLength={500}
                    onChange={(e) =>
                      basicDataFormik.setFieldValue(
                        'company_address',
                        e.target.value
                      )
                    }
                    disabled={!isApplicationEditable()}
                  />
                  <Span variant='danger' type='2'>
                    {basicDataFormik.errors.company_address}
                  </Span>
                </Card>
                <Card width='30vw'>
                  <DropDown
                    id='client-state-dropdown'
                    label='Provincia'
                    items={provinces}
                    size='md'
                    title={basicDataFormik.values.state}
                    onChange={(e) =>
                      basicDataFormik.setFieldValue(
                        'state',
                        provinces.find((item) => item.label === e)?.label
                      )
                    }
                    disabled={!isApplicationEditable()}
                  ></DropDown>
                  <Span variant='danger' type='2'>
                    {basicDataFormik.errors.state}
                  </Span>
                </Card>
                <Card width='30vw'>
                  <Input
                    label='Localidad'
                    id='client-input-city'
                    value={basicDataFormik.values.city}
                    maxLength={300}
                    onChange={(e) =>
                      basicDataFormik.setFieldValue('city', e.target.value)
                    }
                    disabled={!isApplicationEditable()}
                  />
                  <Span variant='danger' type='2'>
                    {basicDataFormik.errors.city}
                  </Span>
                </Card>
              </Card>

              <Card display='flex' margin={{ top: '3' }} gap='24px' alignItems='flex-start'>
                <Card width='30vw'>
                  <Input
                    label='Domicilio legal'
                    id='client-input-legal_company_address'
                    value={basicDataFormik.values.legal_company_address || ''}
                    maxLength={500}
                    onChange={(e) =>
                      basicDataFormik.setFieldValue(
                        'legal_company_address',
                        e.target.value
                      )
                    }
                    disabled={!isApplicationEditable()}
                  />
                </Card>
                <Card width='30vw'>
                  <DropDown
                    id='client-legal_state-dropdown'
                    label='Provincia legal'
                    items={provinces}
                    size='md'
                    title={basicDataFormik.values.legal_state || ''}
                    onChange={(e) =>
                      basicDataFormik.setFieldValue(
                        'legal_state',
                        provinces.find((item) => item.label === e)?.label || ''
                      )
                    }
                    disabled={!isApplicationEditable()}
                  ></DropDown>
                </Card>
                <Card width='30vw'>
                  <Input
                    label='Localidad legal'
                    id='client-input-legal_city'
                    value={basicDataFormik.values.legal_city || ''}
                    maxLength={300}
                    onChange={(e) =>
                      basicDataFormik.setFieldValue('legal_city', e.target.value)
                    }
                    disabled={!isApplicationEditable()}
                  />
                </Card>
              </Card>

              <Card display='grid' gap='8px' margin={{ bottom: '4', top: '4' }}>
                <P
                  type='1'
                  variant='neutral-1'
                  withMargin={false}
                  weight='bold'
                  modifier='strong'
                >
                  Datos de contacto
                </P>
              </Card>

              <Card display='flex' gap='24px' alignItems='flex-start'>
                <Card width='30vw'>
                  <Input
                    label='Número de contacto'
                    id='client-input-phone'
                    value={basicDataFormik.values.phone_number}
                    maxLength={15}
                    onChange={(e) =>
                      basicDataFormik.setFieldValue(
                        'phone_number',
                        e.target.value.replace(/\D/g, '')
                      )
                    }
                    disabled={!isApplicationEditable()}
                  />
                </Card>

                <Card width='30vw'>
                  <Input
                    label='Correo electrónico *'
                    id='client-input-email'
                    value={basicDataFormik.values.email}
                    maxLength={64}
                    onChange={(e) =>
                      basicDataFormik.setFieldValue('email', e.target.value)
                    }
                    disabled={!isApplicationEditable()}
                  />
                  <Span variant='danger' type='2'>
                    {basicDataFormik.errors.email}
                  </Span>
                </Card>
              </Card>
            </Card>
          </form>
          <form onSubmit={societaryFormik.handleSubmit}>
            <P type='1' variant='neutral-1' weight='bold'>
              Composición societaria
            </P>
            <Card
              variant='info'
              modifier='inverted'
              spacing={'3'}
              width='94%'
              margin={{ bottom: '3' }}
            >
              <Card
                display='flex'
                gap='8px'
                alignItems='center'
                margin={{ bottom: '2' }}
              >
                <Icon name='info_circle_outline' variant='info' />
                <P withMargin={false} variant='neutral-1' weight='bold'>
                  Participación
                </P>
              </Card>
              <P type='1' variant='neutral' withMargin={false}>
                Verificá y completá los datos correspondientes recordando que el
                total de participación debe ser igual al 100%{' '}
              </P>
            </Card>
            {renderComposicionSocietaria()}
            <Card
              display='flex'
              justifyContent='space-between'
              margin={{ top: '3' }}
            >
              <Button
                variant='tertiary'
                size='md'
                onClick={addCorporateStructure}
                disabled={!isApplicationEditable()}
              >
                + Agregar socio
              </Button>
              <Checkbox
                checked={clientInfo.has_valid_corporate_composition}
                onChange={() =>
                  (clientInfo.has_valid_corporate_composition =
                    !clientInfo.has_valid_corporate_composition)
                }
                id='basic_data_has_valid_corporate_composition-checkbox'
                disabled={!isApplicationEditable()}
                size='md'
              >
                La información se ajusta a los documentos
              </Checkbox>
            </Card>
            <Card display='flex' justifyContent='flex-end'>
              <Card
                display='flex'
                justifyContent='flex-end'
                gap='16px'
                margin={{ top: '4' }}
                width='420px'
              >
                <Button
                  id='client-button-save'
                  size='lg'
                  variant='secondary'
                  disabled={
                    !basicDataFormik.isValid ||
                    !societaryFormik.isValid ||
                    isRejected ||
                    isApproved ||
                    isFinished ||
                    isSaving
                  }
                  onClick={() => handleSave(false)}
                  fullWidth
                >
                  {isSaving ? (
                    <Spinner size='sm' variant='primary' />
                  ) : (
                    'Guardar'
                  )}
                </Button>
                <Button
                  id='client-button-continue'
                  size='lg'
                  variant='primary'
                  disabled={
                    !basicDataFormik.isValid ||
                    !societaryFormik.isValid ||
                    isSaving
                  }
                  onClick={() => handleSave(true)}
                  fullWidth
                >
                  {isSaving ? (
                    <Spinner size='sm' variant='primary' />
                  ) : (
                    'Continuar'
                  )}
                </Button>
              </Card>
            </Card>
          </form>
        </Card>
      ) : (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
        >
          <Card margin='auto'>
            <Image name='user_empty' />
          </Card>
          <P variant='primary' align='center' weight='bold'>
            No hay datos para mostrar
          </P>
          <P variant='primary' align='center' withMargin={false}>
            Buscá un CUIT para comenzar.
          </P>
        </Card>
      )}
    </Card>
  );
};

export default BasicData;
